import { useTranslation } from 'react-i18next'
import { Loader2, Mail, Bell } from 'lucide-react'
import { toast } from 'react-toastify'
import { useCurrentUser } from '@/features/auth/hooks/use-current-user'
import { useUpdateNotificationPreferences } from '../hooks/use-notification-preferences'
import { cn } from '@/shared/lib/utils'

interface ToggleSwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

function ToggleSwitch({ checked, onChange, disabled }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
        checked ? 'bg-accent' : 'bg-foreground-subtle/30',
        disabled && 'cursor-not-allowed opacity-50'
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5'
        )}
      />
    </button>
  )
}

export function NotificationsTab() {
  const { t } = useTranslation()
  const { user } = useCurrentUser()

  const updatePreferences = useUpdateNotificationPreferences(user?.id || '')

  const handleEmailToggle = async (enabled: boolean) => {
    try {
      await updatePreferences.mutateAsync({
        email_notifications_enabled: enabled,
      })

      toast.success(
        enabled
          ? t('settings.notifications.emailEnabled')
          : t('settings.notifications.emailDisabled')
      )
    } catch {
      toast.error(t('common.error'))
    }
  }

  const emailEnabled = user?.email_notifications_enabled ?? true

  return (
    <div className="flex flex-col gap-6">
      {/* Email Notifications Card */}
      <div className="rounded-xl border border-border bg-background">
        {/* Header */}
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">
            {t('settings.notifications.emailSection')}
          </h2>
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                <Mail className="h-5 w-5 text-accent" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-foreground">
                  {t('settings.notifications.emailToggleLabel')}
                </span>
                <p className="text-sm text-foreground-muted">
                  {t('settings.notifications.emailToggleDescription')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {updatePreferences.isPending && (
                <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" />
              )}
              <ToggleSwitch
                checked={emailEnabled}
                onChange={handleEmailToggle}
                disabled={updatePreferences.isPending}
              />
            </div>
          </div>
        </div>
      </div>

      {/* In-App Notifications Card (informational) */}
      <div className="rounded-xl border border-border bg-background">
        {/* Header */}
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">
            {t('settings.notifications.inAppSection')}
          </h2>
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-foreground-subtle/10">
              <Bell className="h-5 w-5 text-foreground-muted" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-foreground">
                {t('settings.notifications.inAppLabel')}
              </span>
              <p className="text-sm text-foreground-muted">
                {t('settings.notifications.inAppDescription')}
              </p>
              <p className="mt-2 text-xs text-foreground-subtle">
                {t('settings.notifications.inAppHint')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
